import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Circle, Clapperboard, FolderCog, Hourglass, Loader2, RotateCcw, Tv, Folder } from "lucide-react";
import { toast } from "sonner";
import { SelectionBar } from "@/components/SelectionBar";
import { SelectMenu, SelectMark, BarButton, LassoArea, useSelection } from "@/components/Selection";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { VideoCard, VideoThumb, fmtClock } from "@/components/VideoBits";
import { BulkButton, BulkProgress, useBulkJob } from "@/components/BulkArtJob";
import { PosterFinder, useCanPoster } from "@/components/PosterFinder";
import { Rating } from "@/components/TmdbDetails";
import { FolderTabs } from "@/components/FolderTabs";

const GroupCard = ({ g, sel }) => {
  const on = sel.has(g.key);
  return (
  <Link to={`/videos/group/${encodeURIComponent(g.key)}`} data-testid={`video-group-${g.key}`} data-sel-id={g.key} aria-pressed={sel.mode ? on : undefined}
    onClick={(e) => { if (sel.mode) { e.preventDefault(); sel.toggle(g.key, e.shiftKey); } }}
    className={`group flex flex-col rounded-xl p-2 transition-colors hover:bg-white/60 ${on ? "bg-white ring-2 ring-zinc-900" : ""}`}>
    <VideoThumb id={g.cover_id} poster={g.poster} portrait className={`shadow-md transition-transform duration-300 ${sel.mode ? (on ? "scale-[0.96]" : "") : "group-hover:-translate-y-1"}`}>
      {sel.mode && <SelectMark on={on} className="bottom-2 left-2" />}
      <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white backdrop-blur">
        {g.kind === "show" ? <Tv size={10} /> : <Folder size={10} />}{g.kind === "show" ? `${g.seasons.length} season${g.seasons.length === 1 ? "" : "s"}` : "Collection"}
      </span>
      {g.in_progress > 0 && <span className="absolute right-2 top-2 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white">{g.in_progress} in progress</span>}
    </VideoThumb>
    <p className="mt-2 truncate text-sm font-medium text-zinc-900">{g.name}</p>
    <p className="flex items-center gap-1.5 truncate text-xs text-zinc-500"><Rating value={g.tmdb?.rating} /><span className="truncate">{g.count} video{g.count === 1 ? "" : "s"}{g.year ? ` · ${g.year}` : ""}{g.duration ? ` · ${fmtClock(g.duration)}` : ""}{g.watched ? ` · ${g.watched} watched` : ""}</span></p>
  </Link>
  );
};

const Grid = ({ children }) => <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">{children}</div>;

export default function Videos() {
  const { user } = useAuth();
  const canPoster = useCanPoster();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [posters, setPosters] = useState(null);
  const [finder, setFinder] = useState(null); // { target, title, hasPoster }
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    api.get("/videos").then((r) => setData(r.data)).catch((e) => { setErr(formatApiError(e)); setData({ groups: [], total: 0 }); });
    api.get("/posters/status").then((r) => setPosters(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);
  const job = useBulkJob("/posters/fix-all", { noun: "posters", onDone: load });
  const shows = useMemo(() => data?.groups.filter((g) => g.kind === "show") || [], [data]);
  const folders = useMemo(() => data?.groups.filter((g) => g.kind !== "show") || [], [data]);
  // One folder tab per group: "Shows" collects every show; each video folder gets its own tab, so new folders show up as new tabs.
  const tabs = useMemo(() => [
    ...(shows.length ? [{ id: "shows", label: "Shows", icon: Tv, badge: shows.length }] : []),
    ...folders.map((g) => ({ id: g.key, label: g.name, icon: Folder, badge: g.count })),
  ], [shows, folders]);
  const wanted = params.get("tab");
  const tab = tabs.some((t) => t.id === wanted) ? wanted : tabs[0]?.id;
  const folderTab = folders.find((g) => g.key === tab);
  const keys = useMemo(() => [...shows.map((g) => g.key), ...folders.flatMap((g) => g.videos.map((v) => v.id))], [shows, folders]);
  const s = useSelection(keys);
  const [busy, setBusy] = useState(false);
  const bulk = async (action) => {
    setBusy(true);
    try {
      const sel = [...s.sel];
      const { data: r } = await api.post("/videos/bulk", { groups: sel.filter((k) => k.startsWith("show:")), ids: sel.filter((k) => !k.startsWith("show:")), action });
      toast.success(`Marked ${r.count} video${r.count === 1 ? "" : "s"} ${action}`);
      s.exit(); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const vids = folderTab?.videos || [];
  const picks = folderTab ? [
    { label: "Select watched", icon: CheckCircle2, testid: "watched", count: vids.filter((v) => v.progress.finished).length, onClick: () => s.where((v) => v.progress.finished, vids) },
    { label: "Select in progress", icon: Hourglass, testid: "in-progress", count: vids.filter((v) => !v.progress.finished && v.progress.pct > 0).length, onClick: () => s.where((v) => !v.progress.finished && v.progress.pct > 0, vids) },
    { label: "Select unwatched", icon: Circle, testid: "unwatched", count: vids.filter((v) => !v.progress.finished && !(v.progress.pct > 0)).length, onClick: () => s.where((v) => !v.progress.finished && !(v.progress.pct > 0), vids) },
  ] : [
    { label: "Select fully watched", icon: CheckCircle2, testid: "watched", count: shows.filter((g) => g.watched >= g.count).length, onClick: () => s.where((g) => g.watched >= g.count, shows) },
    { label: "Select in progress", icon: Hourglass, testid: "in-progress", count: shows.filter((g) => g.in_progress > 0).length, onClick: () => s.where((g) => g.in_progress > 0, shows) },
  ];
  const noun = folderTab ? "video" : "show";
  const openMoviePoster = (v) => setFinder({ target: { kind: "video", key: v.id }, title: v.title, hasPoster: !!v.poster });
  const eyebrow = data ? `${data.total} video${data.total === 1 ? "" : "s"}${canPoster && posters?.missing ? ` · ${posters.missing} without a poster` : ""}` : "Videos";
  return (
    <div data-testid="videos-page">
      <PageHeader stack eyebrow={eyebrow} title="Videos" testid="videos-header">
        {canPoster && data?.total > 0 && posters?.tmdb && (posters.missing > 0 || posters.details_missing > 0 || job.running) && <BulkButton job={job} label="Find posters for all" testid="videos-fix-posters" />}
        {user?.permissions?.filejump && <Link to="/settings?page=filejump#video-folders" data-testid="videos-folders-link" className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 bg-white px-4 text-sm hover:border-zinc-900"><FolderCog size={15} />Video folders</Link>}
        {data?.total > 0 && <SelectMenu s={s} noun={noun} testid="videos-select" picks={picks} />}
      </PageHeader>
      <SelectionBar count={s.count} hidden={s.hidden} noun="title" busy={busy} onClear={s.none} onSelectAll={!s.allShown ? s.all : null}>
        <BarButton onClick={() => bulk("watched")} icon={CheckCircle2} testid="selection-mark-watched" disabled={busy}>Mark watched</BarButton>
        <BarButton onClick={() => bulk("unwatched")} icon={RotateCcw} testid="selection-mark-unwatched" disabled={busy}>Mark unwatched</BarButton>
      </SelectionBar>
      {canPoster && <BulkProgress job={job} noun="posters" testid="videos-fix-posters-status" onRetry={job.start} />}
      {!data ? <Loader2 className="animate-spin text-zinc-400" />
        : err ? <EmptyState icon={Clapperboard} title="Videos couldn't be loaded" text={err} testid="videos-error" />
        : data.total === 0 ? (
          <EmptyState icon={Clapperboard} title="No videos yet" testid="videos-empty"
            text={user?.permissions?.filejump ? "Pick your FileJump video folders in Settings and press “Scan video folders”. MP4, M4V, MOV and WebM play right here in the browser." : "The library owner hasn't added any videos yet."} />
        ) : (
          <FolderTabs tabs={tabs} value={tab} onChange={(id) => setParams((p) => { p.set("tab", id); return p; }, { replace: true })} testid="video-tabs">
            {(id) => id === "shows" ? (
              <LassoArea s={s} testid="videos-lasso-shows">
                <div data-testid="videos-shows"><Grid>{shows.map((g) => <GroupCard key={g.key} g={g} sel={s} />)}</Grid></div>
              </LassoArea>
            ) : (
              <LassoArea s={s} testid={`videos-lasso-${id}`}>
                <div data-testid={`videos-folder-${id}`}><Grid>{vids.map((v) => <VideoCard key={v.id} v={v} portrait sel={s} onPoster={canPoster && !s.mode ? openMoviePoster : undefined} />)}</Grid></div>
              </LassoArea>
            )}
          </FolderTabs>
        )}
      <PosterFinder target={finder?.target} title={finder?.title} hasPoster={finder?.hasPoster} open={!!finder} onOpenChange={(o) => { if (!o) setFinder(null); }} onApplied={() => { load(); setTimeout(load, 2500); }} />
    </div>
  );
}
